<template>
    <Head :title="`Conduct: ${checklist.template?.title}`" />

    <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <!-- Header -->
        <div class="mb-6">
            <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">{{ checklist.template?.title }}</h1>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {{ checklist.checklist_number }} • {{ checklist.location?.name || 'No location' }}
            </p>
        </div>

        <!-- Template Description -->
        <div v-if="checklist.template?.description" class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
            <p class="text-sm text-blue-900 dark:text-blue-300">{{ checklist.template.description }}</p>
        </div>

        <!-- Sections -->
        <div class="space-y-6">
            <div
                v-for="(section, sIndex) in sortedSections"
                :key="section.id"
                class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700"
            >
                <!-- Section Header -->
                <div class="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                    <h2 class="text-lg font-medium text-gray-900 dark:text-white">
                        {{ sIndex + 1 }}. {{ section.title }}
                    </h2>
                    <p v-if="section.description" class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {{ section.description }}
                    </p>
                </div>

                <!-- Section Fields -->
                <div class="p-6 space-y-6">
                    <div
                        v-for="(field, fIndex) in getVisibleFields(section)"
                        :key="field.id"
                        class="border-b border-gray-200 dark:border-gray-700 pb-6 last:border-0 last:pb-0"
                    >
                        <!-- Field Label -->
                        <label class="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                            {{ sIndex + 1 }}.{{ fIndex + 1 }} {{ field.label }}
                            <span v-if="isFieldRequired(field)" class="text-red-500 ml-1">*</span>
                        </label>

                        <!-- Help Text -->
                        <p v-if="field.help_text" class="text-xs text-gray-500 dark:text-gray-400 mb-3 italic">
                            {{ field.help_text }}
                        </p>

                        <!-- Field Input by Type -->
                        <div>
                            <!-- Yes/No -->
                            <div v-if="field.field_type === 'yes_no'" class="flex space-x-3">
                                <button
                                    @click="setFieldValue(field, 'yes')"
                                    type="button"
                                    :class="[
                                        'px-4 py-2 border rounded-md transition-colors',
                                        responses[field.id] === 'yes'
                                            ? 'bg-blue-500 border-blue-500 text-white'
                                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                                    ]"
                                >
                                    Yes
                                </button>
                                <button
                                    @click="setFieldValue(field, 'no')"
                                    type="button"
                                    :class="[
                                        'px-4 py-2 border rounded-md transition-colors',
                                        responses[field.id] === 'no'
                                            ? 'bg-blue-500 border-blue-500 text-white'
                                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                                    ]"
                                >
                                    No
                                </button>
                            </div>

                            <!-- Pass/Fail/N/A -->
                            <div v-else-if="field.field_type === 'pass_fail_na'" class="flex space-x-3">
                                <button
                                    @click="setFieldValue(field, 'pass')"
                                    type="button"
                                    :class="[
                                        'px-4 py-2 border rounded-md transition-colors',
                                        responses[field.id] === 'pass'
                                            ? 'bg-green-600 border-green-600 text-white'
                                            : 'border-gray-300 dark:border-gray-600 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40'
                                    ]"
                                >
                                    Pass
                                </button>
                                <button
                                    @click="setFieldValue(field, 'fail')"
                                    type="button"
                                    :class="[
                                        'px-4 py-2 border rounded-md transition-colors',
                                        responses[field.id] === 'fail'
                                            ? 'bg-red-600 border-red-600 text-white'
                                            : 'border-gray-300 dark:border-gray-600 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40'
                                    ]"
                                >
                                    Fail
                                </button>
                                <button
                                    @click="setFieldValue(field, 'na')"
                                    type="button"
                                    :class="[
                                        'px-4 py-2 border rounded-md transition-colors',
                                        responses[field.id] === 'na'
                                            ? 'bg-gray-600 border-gray-600 text-white'
                                            : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'
                                    ]"
                                >
                                    N/A
                                </button>
                            </div>

                            <!-- Multiple Choice -->
                            <div v-else-if="field.field_type === 'multiple_choice'" class="space-y-2">
                                <label v-for="option in getFieldOptions(field)" :key="option" class="flex items-center">
                                    <input
                                        v-model="responses[field.id]"
                                        type="radio"
                                        :value="option"
                                        class="rounded-full border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                        @change="saveResponse(field)"
                                    />
                                    <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">{{ option }}</span>
                                </label>
                            </div>

                            <!-- Multiple Selection (Checkboxes) -->
                            <div v-else-if="field.field_type === 'multiple_selection'" class="space-y-2">
                                <label v-for="option in getFieldOptions(field)" :key="option" class="flex items-center">
                                    <input
                                        v-model="checkboxResponses[field.id]"
                                        type="checkbox"
                                        :value="option"
                                        class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                        @change="saveCheckboxResponse(field)"
                                    />
                                    <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">{{ option }}</span>
                                </label>
                            </div>

                            <!-- Dropdown -->
                            <select
                                v-else-if="field.field_type === 'dropdown_select'"
                                v-model="responses[field.id]"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                @change="saveResponse(field)"
                            >
                                <option value="">Select an option...</option>
                                <option v-for="option in getFieldOptions(field)" :key="option" :value="option">
                                    {{ option }}
                                </option>
                            </select>

                            <!-- Short Text -->
                            <input
                                v-else-if="field.field_type === 'text_input'"
                                v-model="responses[field.id]"
                                type="text"
                                placeholder="Enter text..."
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                @blur="saveResponse(field)"
                            />

                            <!-- Long Text -->
                            <textarea
                                v-else-if="field.field_type === 'textarea'"
                                v-model="responses[field.id]"
                                rows="3"
                                placeholder="Enter detailed response..."
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                @blur="saveResponse(field)"
                            />

                            <!-- Number -->
                            <div v-else-if="field.field_type === 'number_input'" class="flex items-center space-x-2">
                                <input
                                    v-model.number="responses[field.id]"
                                    type="number"
                                    placeholder="0"
                                    class="w-32 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    @blur="saveResponse(field)"
                                />
                                <span v-if="getFieldConfig(field).unit" class="text-sm text-gray-600 dark:text-gray-400">
                                    {{ getFieldConfig(field).unit }}
                                </span>
                            </div>

                            <!-- Decimal -->
                            <div v-else-if="field.field_type === 'decimal_input'" class="flex items-center space-x-2">
                                <input
                                    v-model.number="responses[field.id]"
                                    type="number"
                                    step="0.01"
                                    placeholder="0.00"
                                    class="w-32 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    @blur="saveResponse(field)"
                                />
                                <span v-if="getFieldConfig(field).unit" class="text-sm text-gray-600 dark:text-gray-400">
                                    {{ getFieldConfig(field).unit }}
                                </span>
                            </div>

                            <!-- Rating -->
                            <div v-else-if="field.field_type === 'rating_scale'" class="flex space-x-1">
                                <button
                                    v-for="n in (getFieldConfig(field).max || 5)"
                                    :key="n"
                                    type="button"
                                    @click="setRating(field, n)"
                                    class="focus:outline-none transition-colors"
                                >
                                    <StarIcon
                                        v-if="n <= (responses[field.id] || 0)"
                                        class="w-8 h-8 text-yellow-400"
                                    />
                                    <StarIconOutline
                                        v-else
                                        class="w-8 h-8 text-gray-300 dark:text-gray-600 hover:text-yellow-200"
                                    />
                                </button>
                            </div>

                            <!-- Date -->
                            <input
                                v-else-if="field.field_type === 'date_picker'"
                                v-model="responses[field.id]"
                                type="date"
                                class="w-48 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                @change="saveResponse(field)"
                            />

                            <!-- DateTime -->
                            <input
                                v-else-if="field.field_type === 'datetime_picker'"
                                v-model="responses[field.id]"
                                type="datetime-local"
                                class="w-64 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                @change="saveResponse(field)"
                            />

                            <!-- Time -->
                            <input
                                v-else-if="field.field_type === 'time_picker'"
                                v-model="responses[field.id]"
                                type="time"
                                class="w-32 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                @change="saveResponse(field)"
                            />

                            <!-- Photo Capture -->
                            <div v-else-if="field.field_type === 'photo_capture'" class="space-y-4">
                                <!-- Capture Button -->
                                <div class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
                                    <button
                                        @click="openFieldCamera(field.id)"
                                        type="button"
                                        class="w-full focus:outline-none"
                                    >
                                        <svg class="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <p class="text-sm text-gray-600 dark:text-gray-400">Tap to capture photo</p>
                                        <p class="text-xs text-gray-500 dark:text-gray-500 mt-1">Live camera capture only</p>
                                    </button>
                                </div>

                                <!-- Captured Photos Display -->
                                <div v-if="fieldPhotos[field.id] && fieldPhotos[field.id].length > 0" class="space-y-2">
                                    <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Captured Photos ({{ fieldPhotos[field.id].length }})
                                    </h4>
                                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div
                                            v-for="(photo, index) in fieldPhotos[field.id]"
                                            :key="index"
                                            class="relative group border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800 hover:shadow-md transition-shadow"
                                        >
                                            <!-- Image Preview -->
                                            <div class="aspect-video bg-gray-100 dark:bg-gray-900">
                                                <img
                                                    v-if="photo.preview"
                                                    :src="photo.preview"
                                                    alt="Captured photo"
                                                    class="w-full h-full object-cover"
                                                />
                                            </div>

                                            <!-- File Info -->
                                            <div class="p-3">
                                                <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                    {{ photo.file.name }}
                                                </p>
                                                <div class="flex items-center justify-between mt-1">
                                                    <p class="text-xs text-gray-500 dark:text-gray-400">
                                                        {{ formatFileSize(photo.file.size) }}
                                                    </p>
                                                    <button
                                                        @click="removeFieldPhoto(field, index)"
                                                        type="button"
                                                        class="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-xs font-medium"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- File Upload -->
                            <div v-else-if="field.field_type === 'file_upload'" class="space-y-4">
                                <!-- Upload Button -->
                                <div class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
                                    <label :for="`file-upload-${field.id}`" class="cursor-pointer block">
                                        <svg class="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                        <p class="text-sm text-gray-600 dark:text-gray-400">Click to upload file</p>
                                        <p class="text-xs text-gray-500 dark:text-gray-500 mt-1">Documents (PDF, DOC, XLS, etc.) up to 10MB</p>
                                        <input
                                            :id="`file-upload-${field.id}`"
                                            type="file"
                                            class="hidden"
                                            accept="*/*"
                                            @change="handleFieldFileUpload(field, $event)"
                                        />
                                    </label>
                                </div>

                                <!-- Uploaded Files Display -->
                                <div v-if="fieldFiles[field.id] && fieldFiles[field.id].length > 0" class="space-y-2">
                                    <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Uploaded Files ({{ fieldFiles[field.id].length }})
                                    </h4>
                                    <div class="grid grid-cols-1 gap-3">
                                        <div
                                            v-for="(fileObj, index) in fieldFiles[field.id]"
                                            :key="index"
                                            class="relative group border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-800 hover:shadow-md transition-shadow"
                                        >
                                            <div class="flex items-center justify-between">
                                                <div class="flex items-center space-x-3 flex-1 min-w-0">
                                                    <svg class="w-8 h-8 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd" />
                                                    </svg>
                                                    <div class="flex-1 min-w-0">
                                                        <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                            {{ fileObj.file.name }}
                                                        </p>
                                                        <p class="text-xs text-gray-500 dark:text-gray-400">
                                                            {{ formatFileSize(fileObj.file.size) }}
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    @click="removeFieldFile(field, index)"
                                                    type="button"
                                                    class="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-xs font-medium ml-3"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Signature -->
                            <div v-else-if="field.field_type === 'signature'" class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 bg-white dark:bg-gray-700">
                                <p class="text-sm text-gray-600 dark:text-gray-400 text-center mb-2">Sign here</p>
                                <div class="h-24 bg-gray-50 dark:bg-gray-800 rounded"></div>
                                <p v-if="responses[field.id]" class="text-xs text-green-600 dark:text-green-400 text-center mt-2">
                                    ✓ Signed
                                </p>
                            </div>

                            <!-- Default: Text Input (for any unmatched field types) -->
                            <input
                                v-else
                                v-model="responses[field.id]"
                                type="text"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :placeholder="`Enter ${field.field_type || 'text'}`"
                                @blur="saveResponse(field)"
                            />

                            <!-- Field Supplements -->
                            <div class="space-y-3 mt-3">
                                <!-- Notes -->
                                <div v-if="field.allow_notes" class="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        📝 Notes/Comments {{ field.require_notes ? '*' : '(Optional)' }}
                                    </label>
                                    <textarea
                                        v-model="notes[field.id]"
                                        rows="1"
                                        placeholder="Add notes..."
                                        class="w-full text-sm rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    ></textarea>
                                </div>

                                <!-- Photos -->
                                <div v-if="field.allow_photos" class="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                                    <div class="flex items-center justify-between">
                                        <label class="text-xs font-medium text-gray-700 dark:text-gray-300">
                                            📷 Photo {{ field.require_photos ? '*' : '(Optional)' }}
                                        </label>
                                        <div class="flex items-center gap-2">
                                            <div v-if="photos[field.id] && photos[field.id].length > 0" class="flex items-center gap-2">
                                                <span class="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[150px]">
                                                    {{ photos[field.id][0].name }}
                                                </span>
                                                <button
                                                    @click="removeSupplementPhoto(field)"
                                                    type="button"
                                                    class="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                                    title="Remove photo"
                                                >
                                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                            <button
                                                v-if="!photos[field.id] || photos[field.id].length === 0"
                                                @click="openSupplementCamera(field.id)"
                                                type="button"
                                                class="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                                            >
                                                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                <span class="text-xs font-medium">Take Photo</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Attachments -->
                                <div v-if="field.allow_attachments" class="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                                    <div class="flex items-center justify-between">
                                        <label class="text-xs font-medium text-gray-700 dark:text-gray-300">
                                            📎 Attachment {{ field.require_attachments ? '*' : '(Optional)' }}
                                        </label>
                                        <div class="flex items-center gap-2">
                                            <div v-if="attachments[field.id] && attachments[field.id].length > 0" class="flex items-center gap-2">
                                                <span class="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[150px]">
                                                    {{ attachments[field.id][0].name }}
                                                </span>
                                                <button
                                                    @click="removeSupplementAttachment(field)"
                                                    type="button"
                                                    class="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                                    title="Remove attachment"
                                                >
                                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                            <label v-if="!attachments[field.id] || attachments[field.id].length === 0" class="cursor-pointer inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors">
                                                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                                                </svg>
                                                <span class="text-xs font-medium">Upload</span>
                                                <input type="file" class="hidden" @change="handleAttachmentUpload(field, $event)" />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>

        <!-- Actions -->
        <div class="mt-8 flex justify-between items-center bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <Link
                :href="route('checklists.show', checklist.id)"
                class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
                Cancel
            </Link>

            <div class="flex space-x-3">
                <button
                    @click="saveDraft"
                    type="button"
                    :disabled="draftForm.processing"
                    class="inline-flex items-center px-5 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                    </svg>
                    {{ draftForm.processing ? 'Saving...' : 'Save Draft' }}
                </button>

                <button
                    @click="showSubmitModal = true"
                    type="button"
                    :disabled="!canSubmit || submitForm.processing"
                    class="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {{ submitForm.processing ? 'Submitting...' : 'Submit Checklist' }}
                </button>
            </div>
        </div>

        <!-- Submit Confirmation Modal -->
        <Modal
            :show="showSubmitModal"
            title="Submit Checklist"
            size="md"
            @close="showSubmitModal = false"
        >
            <div class="space-y-4">
                <div class="flex items-start">
                    <div class="flex-shrink-0">
                        <svg class="h-12 w-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <div class="ml-4">
                        <p class="text-sm text-gray-700 dark:text-gray-300">
                            Are you sure you want to submit this checklist? You will not be able to edit it after submission.
                        </p>
                    </div>
                </div>
            </div>

            <template #footer="{ close }">
                <button
                    @click="close"
                    type="button"
                    :disabled="submitForm.processing"
                    class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Cancel
                </button>
                <button
                    @click="confirmSubmit"
                    type="button"
                    :disabled="submitForm.processing"
                    class="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {{ submitForm.processing ? 'Submitting...' : 'Submit Checklist' }}
                </button>
            </template>
        </Modal>

        <!-- Camera Capture Modal for Supplement Photos -->
        <CameraCapture
            :show="showSupplementCamera"
            @close="showSupplementCamera = false"
            @captured="handleSupplementCameraCapture"
        />

        <!-- Camera Capture Modal for Field Photos -->
        <CameraCapture
            :show="showFieldCamera"
            @close="showFieldCamera = false"
            @captured="handleFieldCameraCapture"
        />
    </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import route from '@/Utils/route';
import { useSecureForm } from '@/Composables/useSecureForm';
import { StarIcon } from '@heroicons/vue/24/solid';
import { StarIcon as StarIconOutline } from '@heroicons/vue/24/outline';
import Modal from '@/Components/Common/Modal.vue';
import CameraCapture from '@/Components/Checklists/CameraCapture.vue';

const props = defineProps({
    checklist: Object,
    companyOwner: {
        type: Object,
        default: null,
    },
});

// Modal state
const showSubmitModal = ref(false);
const showSupplementCamera = ref(false);
const currentSupplementFieldId = ref(null);
const showFieldCamera = ref(false);
const currentFieldId = ref(null);

// Initialize responses from existing data
const responses = reactive({});
const checkboxResponses = reactive({});
const notes = reactive({});
const photos = reactive({}); // Supplement photos
const attachments = reactive({}); // Supplement attachments
const fieldPhotos = reactive({}); // Field-level photos (photo_capture field type)
const fieldFiles = reactive({}); // Field-level files (file_upload field type)

// Load existing responses
if (props.checklist.responses) {
    props.checklist.responses.forEach(response => {
        responses[response.field_id] = response.response_value;
        notes[response.field_id] = response.notes;

        // Handle checkbox responses (stored as JSON array)
        if (response.field?.field_type === 'multiple_selection' && response.response_value) {
            try {
                checkboxResponses[response.field_id] = JSON.parse(response.response_value);
            } catch (e) {
                checkboxResponses[response.field_id] = [];
            }
        }

        // Load existing photos (supplement photos with attachment_type = 'photo')
        if (response.photos && response.photos.length > 0) {
            photos[response.field_id] = response.photos.map(photo => ({
                id: photo.id,
                name: photo.original_filename,
                file_path: photo.file_path,
            }));
        }

        // Load existing attachments (supplement files with attachment_type = 'file')
        if (response.attachments && response.attachments.length > 0) {
            const supplementFiles = response.attachments.filter(att => att.attachment_type === 'file');
            if (supplementFiles.length > 0) {
                attachments[response.field_id] = supplementFiles.map(file => ({
                    id: file.id,
                    name: file.original_filename,
                    file_path: file.file_path,
                }));
            }
        }
    });
}

// Initialize all checkbox fields as arrays (even if no existing response)
props.checklist.template?.sections?.forEach(section => {
    section.fields?.forEach(field => {
        if (field.field_type === 'multiple_selection' && !checkboxResponses[field.id]) {
            checkboxResponses[field.id] = [];
        }
    });
});

// Forms for secure submissions
const draftForm = useSecureForm({
    responses: {},
});

const submitForm = useSecureForm({
    responses: {},
});

const sortedSections = computed(() => {
    if (!props.checklist.template?.sections) return [];
    return [...props.checklist.template.sections].sort((a, b) => a.display_order - b.display_order);
});

const sortedFields = (section) => {
    if (!section.fields) return [];
    return [...section.fields].sort((a, b) => a.display_order - b.display_order);
};

// Helper: Get field config (parse if JSON string)
const getFieldConfig = (field) => {
    if (!field.field_config) return {};
    return typeof field.field_config === 'string' ? JSON.parse(field.field_config) : field.field_config;
};

// Helper: Get field options
const getFieldOptions = (field) => {
    const config = getFieldConfig(field);
    return config.options || [];
};

// Helper: Evaluate if field dependency condition is met (case-insensitive)
const evaluateFieldDependency = (field, parentValue) => {
    if (!field.dependency_rule) return true;

    const rule = typeof field.dependency_rule === 'string'
        ? JSON.parse(field.dependency_rule)
        : field.dependency_rule;

    if (!rule || !rule.condition || !rule.value) return true;

    const normalizedParent = String(parentValue || '').toLowerCase();
    const normalizedExpected = String(rule.value).toLowerCase();

    switch (rule.condition) {
        case 'equals':
            return normalizedParent === normalizedExpected;
        case 'not_equals':
            return normalizedParent !== normalizedExpected;
        case 'contains':
            return normalizedParent.includes(normalizedExpected);
        case 'greater_than':
            return parseFloat(parentValue) > parseFloat(rule.value);
        case 'less_than':
            return parseFloat(parentValue) < parseFloat(rule.value);
        default:
            return true;
    }
};

// Helper: Check if field should be visible based on dependency rules
const isFieldVisible = (field) => {
    // Primary fields (no dependency) are always visible
    if (!field.parent_field_id || !field.dependency_rule) return true;

    const parentValue = responses[field.parent_field_id];

    // If parent hasn't been answered, hide dependent field
    if (parentValue === undefined || parentValue === null || parentValue === '') return false;

    // Evaluate the dependency condition
    return evaluateFieldDependency(field, parentValue);
};

// Helper: Get visible fields for a section (sorted and filtered by visibility)
const getVisibleFields = (section) => {
    if (!section.fields) return [];
    const sorted = [...section.fields].sort((a, b) => a.display_order - b.display_order);
    return sorted.filter(field => isFieldVisible(field));
};

// Helper: Check if field is required
// Primary fields: use is_required column
// Dependent fields: use dependency rule action only
const isFieldRequired = (field) => {
    if (!field) return false;

    // Dependent field - use dependency rule action only
    if (field.parent_field_id && field.dependency_rule) {
        const rule = typeof field.dependency_rule === 'string'
            ? JSON.parse(field.dependency_rule)
            : field.dependency_rule;

        return rule?.action === 'show_and_require';
    }

    // Primary field - use is_required column
    return field.is_required === true || field.is_required === 1;
};

// Helper: Get attachments for a field
const getFieldAttachments = (field, type) => {
    if (!props.checklist.responses) return [];

    // Find the response for this field
    const response = props.checklist.responses.find(r => r.template_field_id === field.id);
    if (!response) return [];

    // Return attachments of the specified type
    if (type === 'photo') {
        return response.photos || [];
    } else if (type === 'file') {
        return response.attachments?.filter(a => a.attachment_type === 'file') || [];
    }

    return [];
};

const canSubmit = computed(() => {
    // Check if all required and visible fields are filled
    const allSections = props.checklist.template?.sections || [];

    for (const section of allSections) {
        const fields = section.fields || [];

        for (const field of fields) {
            // Check if field is visible
            const visible = isFieldVisible(field);

            // Only validate if field is visible
            if (!visible) continue;

            // Check if field is required
            const required = isFieldRequired(field);

            // Check if required field is filled
            if (required) {
                // For photo_capture field type, check fieldPhotos
                if (field.field_type === 'photo_capture') {
                    if (!fieldPhotos[field.id] || fieldPhotos[field.id].length === 0) {
                        console.log(`Validation failed: photo_capture field ${field.id} (${field.label}) has no photos`);
                        return false;
                    }
                }
                // For file_upload field type, check fieldFiles
                else if (field.field_type === 'file_upload') {
                    if (!fieldFiles[field.id] || fieldFiles[field.id].length === 0) {
                        console.log(`Validation failed: file_upload field ${field.id} (${field.label}) has no files`);
                        return false;
                    }
                }
                // For multiple_selection, check if at least one option is selected
                else if (field.field_type === 'multiple_selection') {
                    if (!responses[field.id] || responses[field.id] === '[]') {
                        console.log(`Validation failed: multiple_selection field ${field.id} (${field.label}) has no selections`);
                        return false;
                    }
                }
                // For other field types, check responses (but allow 0 as valid)
                else {
                    const value = responses[field.id];
                    if (value === undefined || value === null || value === '') {
                        console.log(`Validation failed: field ${field.id} (${field.label}) type ${field.field_type} is empty`);
                        return false;
                    }
                }
            }

            // Check if required notes are filled
            if (field.require_notes && (!notes[field.id] || notes[field.id].trim() === '')) {
                console.log(`Validation failed: field ${field.id} (${field.label}) requires notes`);
                return false;
            }

            // Check if required photos are uploaded (supplement photos)
            if (field.require_photos && (!photos[field.id] || photos[field.id].length === 0)) {
                console.log(`Validation failed: field ${field.id} (${field.label}) requires supplement photos`);
                return false;
            }

            // Check if required attachments are uploaded (supplement attachments)
            if (field.require_attachments && (!attachments[field.id] || attachments[field.id].length === 0)) {
                console.log(`Validation failed: field ${field.id} (${field.label}) requires supplement attachments`);
                return false;
            }
        }
    }

    console.log('All validations passed');
    return true;
});

// Save individual field response (auto-save functionality)
// Clear dependent fields recursively when parent field changes
const clearDependentFields = (parentFieldId) => {
    // Find all fields that depend on this parent field
    const allSections = props.checklist.template?.sections || [];

    allSections.forEach(section => {
        if (!section.fields) return;

        section.fields.forEach(field => {
            // Check if this field depends on the parent
            if (field.parent_field_id === parentFieldId && field.dependency_rule) {
                // Check if this field should now be hidden
                if (!isFieldVisible(field)) {
                    // Clear the field value
                    delete responses[field.id];

                    // Clear checkbox responses if applicable
                    if (checkboxResponses[field.id]) {
                        checkboxResponses[field.id] = [];
                    }

                    // Clear notes
                    if (notes[field.id]) {
                        delete notes[field.id];
                    }

                    // Clear supplement photos
                    if (photos[field.id]) {
                        delete photos[field.id];
                    }

                    // Clear supplement attachments
                    if (attachments[field.id]) {
                        delete attachments[field.id];
                    }

                    // Clear field photos (photo_capture field type)
                    if (fieldPhotos[field.id]) {
                        // Revoke preview URLs to free memory
                        fieldPhotos[field.id].forEach(photoObj => {
                            if (photoObj.preview) {
                                URL.revokeObjectURL(photoObj.preview);
                            }
                        });
                        delete fieldPhotos[field.id];
                    }

                    // Clear field files (file_upload field type)
                    if (fieldFiles[field.id]) {
                        delete fieldFiles[field.id];
                    }

                    console.log(`Cleared dependent field ${field.id} (${field.label})`);

                    // Recursively clear any fields that depend on this field
                    clearDependentFields(field.id);
                }
            }
        });
    });
};

const saveResponse = (field) => {
    // Auto-save the field response
    // Currently just a placeholder - actual saving happens on Save Draft or Submit
    // This can be enhanced later to auto-save to the server
    console.log('Field response updated:', field.id, responses[field.id]);

    // Clear any dependent fields that should now be hidden
    clearDependentFields(field.id);
};

// Update field value (for button-based fields like yes/no, pass/fail/na)
const setFieldValue = (field, value) => {
    responses[field.id] = value;
    saveResponse(field);
};

// Update checkbox responses (just update local state)
const saveCheckboxResponse = (field) => {
    responses[field.id] = JSON.stringify(checkboxResponses[field.id] || []);
    saveResponse(field);
};

// Update rating responses (just update local state)
const setRating = (field, rating) => {
    responses[field.id] = rating;
    saveResponse(field);
};

// Handle file upload (just update local state)
const handleFileUpload = (field, event) => {
    const file = event.target.files[0];
    if (file) {
        // In a real implementation, this would upload the file
        // For now, just mark as uploaded
        responses[field.id] = file.name;
    }
};

// Handle photo upload for field supplements (single file only)
const handlePhotoUpload = (field, event) => {
    const file = event.target.files[0];
    if (file) {
        // Store File object locally - will be uploaded when saving draft or submitting
        photos[field.id] = [file];
        console.log(`Photo selected for field ${field.id}: ${file.name}`);
    }
};

// Open camera for supplement photo
const openSupplementCamera = (fieldId) => {
    currentSupplementFieldId.value = fieldId;
    showSupplementCamera.value = true;
};

// Handle camera capture for supplement photo
const handleSupplementCameraCapture = (file) => {
    if (currentSupplementFieldId.value) {
        photos[currentSupplementFieldId.value] = [file];
        console.log(`Photo captured for field ${currentSupplementFieldId.value}: ${file.name}`);
    }
    currentSupplementFieldId.value = null;
};

// Remove supplement photo
const removeSupplementPhoto = (field) => {
    delete photos[field.id];
    console.log(`Photo removed for field ${field.id}`);
};

// Handle attachment upload for field supplements (single file only)
const handleAttachmentUpload = (field, event) => {
    const file = event.target.files[0];
    if (file) {
        // Store File object locally - will be uploaded when saving draft or submitting
        attachments[field.id] = [file];
        console.log(`Attachment selected for field ${field.id}: ${file.name}`);
    }
};

// Remove supplement attachment
const removeSupplementAttachment = (field) => {
    delete attachments[field.id];
    console.log(`Attachment removed for field ${field.id}`);
};

// Open camera for field photo capture
const openFieldCamera = (fieldId) => {
    currentFieldId.value = fieldId;
    showFieldCamera.value = true;
};

// Handle camera capture for field photo
const handleFieldCameraCapture = (file) => {
    if (currentFieldId.value) {
        // Create preview URL for the captured photo
        const preview = URL.createObjectURL(file);

        // Initialize array if it doesn't exist
        if (!fieldPhotos[currentFieldId.value]) {
            fieldPhotos[currentFieldId.value] = [];
        }

        // Add the photo with preview
        fieldPhotos[currentFieldId.value].push({
            file: file,
            preview: preview
        });

        console.log(`Photo captured for field ${currentFieldId.value}: ${file.name}`);
    }
    currentFieldId.value = null;
};

// Remove field photo
const removeFieldPhoto = (field, index) => {
    if (fieldPhotos[field.id] && fieldPhotos[field.id][index]) {
        // Revoke the preview URL to free memory
        if (fieldPhotos[field.id][index].preview) {
            URL.revokeObjectURL(fieldPhotos[field.id][index].preview);
        }

        fieldPhotos[field.id].splice(index, 1);

        // Clean up empty array
        if (fieldPhotos[field.id].length === 0) {
            delete fieldPhotos[field.id];
        }

        console.log(`Photo removed from field ${field.id}`);
    }
};

// Handle file upload for field file_upload
const handleFieldFileUpload = (field, event) => {
    const file = event.target.files[0];
    if (file) {
        // Validate file size (10MB max)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            alert('File is too large. Maximum size is 10MB.');
            event.target.value = '';
            return;
        }

        // Initialize array if it doesn't exist
        if (!fieldFiles[field.id]) {
            fieldFiles[field.id] = [];
        }

        // Add the file
        fieldFiles[field.id].push({
            file: file
        });

        console.log(`File selected for field ${field.id}: ${file.name}`);
        event.target.value = ''; // Reset input
    }
};

// Remove field file
const removeFieldFile = (field, index) => {
    if (fieldFiles[field.id] && fieldFiles[field.id][index]) {
        fieldFiles[field.id].splice(index, 1);

        // Clean up empty array
        if (fieldFiles[field.id].length === 0) {
            delete fieldFiles[field.id];
        }

        console.log(`File removed from field ${field.id}`);
    }
};

// Format file size helper
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

// Save draft - saves all responses to database without submitting
const saveDraft = async () => {
    // Prepare all responses
    const allResponses = [];
    const allSections = props.checklist.template?.sections || [];

    for (const section of allSections) {
        const fields = section.fields || [];

        for (const field of fields) {
            // Include field if it has a response value
            if (responses[field.id] !== undefined && responses[field.id] !== null && responses[field.id] !== '') {
                allResponses.push({
                    field_id: field.id,
                    response_value: responses[field.id],
                    comments: notes[field.id] || null, // Backend expects 'comments' key but saves to 'notes' column
                });
            }
            // Also include photo_capture/file_upload fields if they have notes or files
            // These fields don't have response_value but need to save notes
            else if ((field.field_type === 'photo_capture' || field.field_type === 'file_upload') &&
                     (notes[field.id] || fieldPhotos[field.id]?.length > 0 || fieldFiles[field.id]?.length > 0)) {
                allResponses.push({
                    field_id: field.id,
                    response_value: '', // Empty value for file fields
                    comments: notes[field.id] || null,
                });
            }
        }
    }

    // Create FormData for file upload support
    const formData = new FormData();
    formData.append('responses', JSON.stringify(allResponses));

    // Add supplement photos
    for (const fieldId in photos) {
        if (photos[fieldId] && photos[fieldId].length > 0 && photos[fieldId][0] instanceof File) {
            formData.append(`photos[${fieldId}]`, photos[fieldId][0]);
        }
    }

    // Add supplement attachments
    for (const fieldId in attachments) {
        if (attachments[fieldId] && attachments[fieldId].length > 0 && attachments[fieldId][0] instanceof File) {
            formData.append(`attachments[${fieldId}]`, attachments[fieldId][0]);
        }
    }

    // Add field photos (photo_capture field type)
    for (const fieldId in fieldPhotos) {
        if (fieldPhotos[fieldId] && fieldPhotos[fieldId].length > 0) {
            fieldPhotos[fieldId].forEach((photoObj, index) => {
                if (photoObj.file instanceof File) {
                    formData.append(`field_photos[${fieldId}][${index}]`, photoObj.file);
                }
            });
        }
    }

    // Add field files (file_upload field type)
    for (const fieldId in fieldFiles) {
        if (fieldFiles[fieldId] && fieldFiles[fieldId].length > 0) {
            fieldFiles[fieldId].forEach((fileObj, index) => {
                if (fileObj.file instanceof File) {
                    formData.append(`field_files[${fieldId}][${index}]`, fileObj.file);
                }
            });
        }
    }

    try {
        const response = await axios.post(
            route('checklists.save-draft', props.checklist.id),
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );

        if (response.data.success) {
            alert('Draft saved successfully!');
            // Reload page to refresh with saved data
            window.location.reload();
        }
    } catch (error) {
        console.error('Failed to save draft:', error);
        alert(error.response?.data?.message || 'Failed to save draft. Please try again.');
    }
};

// Confirm and submit checklist - saves all responses and submits for approval/completion
const confirmSubmit = async () => {
    // Double-check validation before submitting
    if (!canSubmit.value) {
        alert('Please complete all required fields before submitting.');
        showSubmitModal.value = false;
        return;
    }

    // Prepare all responses
    const allResponses = [];
    const allSections = props.checklist.template?.sections || [];

    for (const section of allSections) {
        const fields = section.fields || [];

        for (const field of fields) {
            // Only include visible fields
            if (!isFieldVisible(field)) continue;

            // Include field if it has a response value
            if (responses[field.id] !== undefined && responses[field.id] !== null && responses[field.id] !== '') {
                allResponses.push({
                    field_id: field.id,
                    response_value: responses[field.id],
                    comments: notes[field.id] || null, // Backend expects 'comments' key but saves to 'notes' column
                });
            }
            // Also include photo_capture/file_upload fields if they have notes or files
            // These fields don't have response_value but need to save notes
            else if ((field.field_type === 'photo_capture' || field.field_type === 'file_upload') &&
                     (notes[field.id] || fieldPhotos[field.id]?.length > 0 || fieldFiles[field.id]?.length > 0)) {
                allResponses.push({
                    field_id: field.id,
                    response_value: '', // Empty value for file fields
                    comments: notes[field.id] || null,
                });
            }
        }
    }

    // Create FormData for file upload support
    const formData = new FormData();
    formData.append('responses', JSON.stringify(allResponses));

    // Add supplement photos
    for (const fieldId in photos) {
        if (photos[fieldId] && photos[fieldId].length > 0 && photos[fieldId][0] instanceof File) {
            formData.append(`photos[${fieldId}]`, photos[fieldId][0]);
        }
    }

    // Add supplement attachments
    for (const fieldId in attachments) {
        if (attachments[fieldId] && attachments[fieldId].length > 0 && attachments[fieldId][0] instanceof File) {
            formData.append(`attachments[${fieldId}]`, attachments[fieldId][0]);
        }
    }

    // Debug: Log fieldPhotos and fieldFiles before appending
    console.log('fieldPhotos before appending:', fieldPhotos);
    console.log('fieldFiles before appending:', fieldFiles);

    // Add field photos (photo_capture field type)
    for (const fieldId in fieldPhotos) {
        console.log(`Checking fieldPhotos[${fieldId}]:`, fieldPhotos[fieldId]);
        if (fieldPhotos[fieldId] && fieldPhotos[fieldId].length > 0) {
            fieldPhotos[fieldId].forEach((photoObj, index) => {
                console.log(`  Photo ${index}:`, photoObj, 'Is File?', photoObj.file instanceof File);
                if (photoObj.file instanceof File) {
                    formData.append(`field_photos[${fieldId}][${index}]`, photoObj.file);
                    console.log(`  Appended field_photos[${fieldId}][${index}]`, photoObj.file.name);
                }
            });
        }
    }

    // Add field files (file_upload field type)
    for (const fieldId in fieldFiles) {
        console.log(`Checking fieldFiles[${fieldId}]:`, fieldFiles[fieldId]);
        if (fieldFiles[fieldId] && fieldFiles[fieldId].length > 0) {
            fieldFiles[fieldId].forEach((fileObj, index) => {
                console.log(`  File ${index}:`, fileObj, 'Is File?', fileObj.file instanceof File);
                if (fileObj.file instanceof File) {
                    formData.append(`field_files[${fieldId}][${index}]`, fileObj.file);
                    console.log(`  Appended field_files[${fieldId}][${index}]`, fileObj.file.name);
                }
            });
        }
    }

    try {
        const response = await axios.post(
            route('checklists.submit', props.checklist.id),
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );

        if (response.data) {
            showSubmitModal.value = false;
            // Redirect to checklists index page
            window.location.href = route('checklists.index');
        }
    } catch (error) {
        console.error('Failed to submit checklist:', error);
        console.error('Error response:', error.response?.data);

        // Show detailed validation errors if available
        if (error.response?.data?.errors) {
            const errors = error.response.data.errors;
            const errorMessages = Object.entries(errors)
                .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
                .join('\n');
            alert(`Validation Errors:\n\n${errorMessages}`);
        } else {
            alert(error.response?.data?.message || 'Failed to submit checklist. Please try again.');
        }

        showSubmitModal.value = false;
    }
};
</script>
